import { a as parseConfig, c as validateConfig, i as mapToKeyValues, n as getEnvString, o as restoreUnknownFields, r as mapFromKeyValues, s as stripTypeNames, t as Config } from "./chunk-config.mjs";
import { n as FILE_DYNAMICS, r as FILE_ENVS, t as FILE_DEFAULTS } from "./chunk-types.mjs";
import { dirname } from "@dxos/node-std/path";
import * as defs from "@dxos/protocols/buf/dxos/config_pb";
import { isNode } from "@dxos/util";
import { invariant } from "@dxos/invariant";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as FileSystem from "effect/FileSystem";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import { DEFAULT_HUB_URL, DX_CONFIG, DX_DATA, getProfileConfigPath, getProfilePath } from "@dxos/client-protocol";
import * as Match from "effect/Match";
export * from "#loaders";
export * from "#savers";
export * from "#plugin";
//#region src/edge-services.ts
/**
* Canonical names for EDGE (Cloudflare Worker) services.
* Used as keys into `runtime.services.edgeServices` and into the path table below.
*/
var EdgeServiceName = Object.freeze({
	Calls: "calls",
	Image: "image",
	Transcription: "transcription",
	VideoTranscription: "video-transcription",
	Discord: "discord",
	CorsProxy: "cors-proxy",
	Introspect: "introspect",
	Sandbox: "sandbox"
});
/**
* The EDGE entrypoint per environment. `local` is `moon run edge:dev`, and `main.dxos.network`
* remains an alias of `preview` for clients in the field.
*/
var EDGE_URLS = Object.freeze({
	local: "http://localhost:8787",
	dev: "https://dev.dxos.network",
	preview: "https://preview.dxos.network",
	production: "https://dxos.network"
});
/**
* The path each service answers on beneath the EDGE entrypoint.
*
* EDGE is the front door for every service, and each owns exactly one prefix on it, so a client needs
* one base URL per environment rather than a hostname per service — a hostname per service has to be
* redeployed every time one of them moves, a prefix table does not. See
* `edge/docs/design/system/http-route-migration.md`.
*
* The entries that are not simply `/<name>`:
*
* - `transcription` is the ASR endpoint (`POST /transcribe`), which calls-service serves — the same
*   worker `calls` reaches, which is why it has always been configured to the same host.
* - `video-transcription` is the separate transcription-service (`GET /video`), whose prefix is the
*   worker's own name.
* - `introspect` carries the `/mcp` path because the value IS the MCP server URL a client connects to.
*/
var EDGE_SERVICE_PATHS = Object.freeze({
	[EdgeServiceName.Calls]: "/calls",
	[EdgeServiceName.Image]: "/image",
	[EdgeServiceName.Transcription]: "/calls",
	[EdgeServiceName.VideoTranscription]: "/transcription",
	[EdgeServiceName.Discord]: "/discord",
	[EdgeServiceName.CorsProxy]: "/cors-proxy",
	[EdgeServiceName.Introspect]: "/introspect/mcp",
	[EdgeServiceName.Sandbox]: "/sandbox"
});
/** Join a service path onto a base URL, tolerating a trailing slash on the base (`dx.yml` has one). */
var joinEdgePath = (baseUrl, path) => `${baseUrl.replace(/\/+$/, "")}${path}`;
/**
* Resolve an EDGE service endpoint, `undefined` when nothing is configured — absence still means the
* feature is unavailable, so an unconfigured client reaches no network at all.
*
* Two sources, in order:
*
* 1. An explicit `runtime.services.edgeServices` entry. The last match wins, since proto cannot enforce
*    uniqueness on a repeated field. This is the override — a local worker, a one-off host.
* 2. `runtime.services.edge.url` plus the service's path. This is the normal case: configuring the EDGE
*    entrypoint configures every service behind it, and no service endpoint can drift to a different
*    environment than the client's own.
*/
var getEdgeServiceEndpoint = (config, name) => {
	const configured = config.values.runtime?.services?.edgeServices?.findLast((service) => service.name === name)?.endpoint;
	if (configured) return configured;
	const edgeUrl = config.values.runtime?.services?.edge?.url;
	return edgeUrl ? joinEdgePath(edgeUrl, EDGE_SERVICE_PATHS[name]) : void 0;
};
//#endregion
//#region src/config-service.ts
var __dxlog_file = "/__w/dxos/dxos/packages/sdk/config/src/config-service.ts";
var memoryConfig = new Config({ runtime: { client: { edgeFeatures: {
	subductionReplicator: true,
	feedReplicator: true,
	signaling: true,
	agents: true
} } } });
/**
* Endpoints the CLI writes into a profile when it creates one, kept out of
* {@link profileBuiltinDefaults} so the user can see and change what their CLI talks to.
*/
var defaultProfileEndpoints = new Config({ runtime: { services: {
	hub: { url: DEFAULT_HUB_URL },
	edge: { url: `${EDGE_URLS.production}/` },
	iceProviders: [{ urls: `${EDGE_URLS.production}/ice` }],
	ipfs: {
		server: "https://api.ipfs.dxos.network/api/v0",
		gateway: "https://gateway.ipfs.dxos.network/ipfs"
	}
} } });
/** Aligns a fresh monorepo CLI profile with the backend Composer's local dev server talks to. */
var localDevConfig = new Config({ runtime: { services: { edge: { url: EDGE_URLS.preview } } } }, defaultProfileEndpoints.values);
var ConfigService = class extends Context.Service()("ConfigService") {
	static load = (args) => {
		const defaultConfigPath = getProfileConfigPath(DX_CONFIG, args.profile);
		return Effect.gen(function* () {
			const fs = yield* FileSystem.FileSystem;
			const Yaml = yield* Effect.promise(() => import("yaml"));
			const configPath = Option.getOrElse(args.config, () => defaultConfigPath);
			const configContent = yield* fs.readFileString(configPath);
			return withProfileDefaults(Yaml.parse(configContent), args.profile);
		}).pipe(Effect.catchTag("PlatformError", (error) => error.reason._tag === "BadArgument" ? Effect.fail(error) : Effect.gen(function* () {
			const Yaml = yield* Effect.promise(() => import("yaml"));
			const configValues = (process.env.DX_LOCAL_DEV !== void 0 && process.env.DX_LOCAL_DEV !== "0" ? localDevConfig : defaultProfileEndpoints).values;
			const fs = yield* FileSystem.FileSystem;
			const pathToCreate = Option.getOrElse(args.config, () => defaultConfigPath);
			yield* fs.makeDirectory(dirname(pathToCreate), { recursive: true });
			yield* fs.writeFileString(pathToCreate, Yaml.stringify(configValues));
			return withProfileDefaults(configValues, args.profile);
		})));
	};
};
var layerMemory = Layer.effect(ConfigService, Effect.succeed(memoryConfig));
var fromConfig = (config) => Layer.succeed(ConfigService, config);
/**
* Both load branches (existing file, first-run write) must layer env, file, and builtins in the same
* order, or a freshly created profile would come up without storage or the hub.
*/
var withProfileDefaults = (configValues, profile) => ConfigService.of(new Config(processEnvDefaults(), configValues, profileBuiltinDefaults(profile).values));
/**
* `DX_*` process env projected onto `runtime.app.env`, mirroring what the bundler config plugin does
* for browser builds — without it those keys are unreachable on node, where nothing bundles the app.
* Takes precedence over the profile config file, so `DX_HUB_URL=… dx …` overrides for one command.
*/
var processEnvDefaults = () => {
	const env = {};
	for (const [key, value] of Object.entries(process.env)) if (key.startsWith("DX_") && value !== void 0) env[key] = value;
	return Object.keys(env).length > 0 ? { runtime: { app: { env } } } : {};
};
/**
* Default config for a profile.
* Always merged with the default config.
*/
var profileBuiltinDefaults = (profile) => {
	invariant(!profile.endsWith(".yml"), void 0, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file,
		L: 131,
		S: void 0,
		A: ["!profile.endsWith('.yml')", ""]
	});
	return new Config({ runtime: {
		services: { hub: { url: DEFAULT_HUB_URL } },
		client: {
			edgeFeatures: {
				subductionReplicator: true,
				feedReplicator: true,
				signaling: true,
				agents: true
			},
			storage: {
				persistent: true,
				dataRoot: getProfilePath(DX_DATA, profile)
			}
		}
	} });
};
//#endregion
//#region src/telemetry.ts
/**
* Resolves the telemetry tag used as the `X-DXOS-Client-Tag` header on edge
* requests AND as the `ctx.tag` attribute / resource on observability traces.
*
* Keeping a single resolver ensures the same tag identifies a session across
* edge and telemetry tiers. Precedence:
*
*   1. `config.runtime.app.env.DX_TELEMETRY_TAG` — the explicit, deterministic
*      path. Populated from `.env`/build-time by the app's config loaders.
*   2. `process.env.DX_TELEMETRY_TAG` — Node-only, for dev/CI convenience.
*
* Returns `undefined` when unset; callers should no-op on undefined rather
* than stamping empty strings.
*/
var resolveTelemetryTag = (config) => {
	return getEnvString(config, "DX_TELEMETRY_TAG") ?? (isNode() ? process.env.DX_TELEMETRY_TAG : void 0);
};
//#endregion
//#region src/preset.ts
var edgeUrl = (edge) => Match.value(edge).pipe(Match.when("local", () => "http://localhost:8787"), Match.when("dev", () => EDGE_URLS.dev), Match.when("preview", () => EDGE_URLS.preview), Match.when("main", () => EDGE_URLS.preview), Match.when("production", () => EDGE_URLS.production), Match.exhaustive);
var sandboxUrl = (sandbox) => Match.value(sandbox).pipe(Match.when("local", () => "http://localhost:8792"), Match.orElse(() => void 0));
var configPreset = ({ edge = "preview", sandbox } = {}) => {
	const sandboxOverride = sandbox && sandboxUrl(sandbox);
	return new Config({
		version: 1,
		runtime: {
			client: { edgeFeatures: {
				signaling: true,
				subductionReplicator: true,
				feedReplicator: true
			} },
			services: {
				edge: { url: edgeUrl(edge) },
				...sandboxOverride ? { sandbox: { url: sandboxOverride } } : {}
			}
		}
	});
};
//#endregion
export { Config, ConfigService, EDGE_URLS, EdgeServiceName, FILE_DEFAULTS, FILE_DYNAMICS, FILE_ENVS, configPreset, defaultProfileEndpoints, defs, fromConfig, getEdgeServiceEndpoint, getEnvString, layerMemory, localDevConfig, mapFromKeyValues, mapToKeyValues, memoryConfig, parseConfig, resolveTelemetryTag, restoreUnknownFields, stripTypeNames, validateConfig };

//# sourceMappingURL=index.mjs.map